import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import {
  DownloadRounded,
  InfoOutlined,
  PictureAsPdfOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  documentCraftingFileUrls,
  downloadDocumentCraftingRevisionFile,
} from "../../../Api/documentCrafting";
import { downloadDocumentBlob } from "../../../Helpers/documentHelper";
import {
  DocumentCraftingFileKindPath,
  DocumentCraftingRevisionArtefacts,
} from "../../../Models/documentCrafting";
import { downloadFileName } from "../Common/format";
import PdfViewerDialog from "./PdfViewerDialog";

interface Props {
  documentId: number;
  title: string;
  revisionId: number;
  /** null for a draft. */
  revisionNumber: number | null;
  /** From the revision detail; undefined while it loads. */
  artefacts: DocumentCraftingRevisionArtefacts | undefined;
  nativeExt: string;
  /** Title of the PDF viewer, e.g. "Revision 3". */
  viewerTitle: string;
  /** Further buttons in the row, e.g. the translation preview. */
  children?: React.ReactNode;
}

/** "Open PDF" plus the downloads of one revision (native format and PDF),
 * with the hint that the fonts are not embedded in DOCX/PPTX. */
const RevisionDownloads: React.FC<Props> = ({
  documentId,
  title,
  revisionId,
  revisionNumber,
  artefacts,
  nativeExt,
  viewerTitle,
  children,
}) => {
  const { t } = useTranslation("documentCrafting");
  const [downloading, setDownloading] =
    useState<DocumentCraftingFileKindPath | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const ext = artefacts?.native_ext || nativeExt;
  const suffix = revisionNumber === null ? t("detail.downloads.draftSuffix") : undefined;

  const download = async (kind: DocumentCraftingFileKindPath) => {
    setDownloading(kind);
    try {
      const response = await downloadDocumentCraftingRevisionFile(
        documentId,
        revisionId,
        kind,
      );
      downloadDocumentBlob(
        response,
        downloadFileName(title, revisionNumber, kind === "pdf" || kind === "png" ? kind : ext, suffix),
      );
    } catch {
      // The request shows the error snack.
    } finally {
      setDownloading(null);
    }
  };

  const busyIcon = (kind: DocumentCraftingFileKindPath, icon: React.ReactNode) =>
    downloading === kind ? <CircularProgress size={14} /> : icon;

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        <Button
          size="small"
          variant="contained"
          startIcon={<PictureAsPdfOutlined />}
          onClick={() => setPdfOpen(true)}
          disabled={!artefacts?.pdf}
        >
          {t("detail.preview.openPdf")}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={busyIcon("native", <DownloadRounded />)}
          onClick={() => download("native")}
          disabled={!artefacts?.native || downloading !== null}
        >
          {t("detail.downloads.native", { ext: ext.toUpperCase() })}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={busyIcon("pdf", <DownloadRounded />)}
          onClick={() => download("pdf")}
          disabled={!artefacts?.pdf || downloading !== null}
        >
          {t("detail.downloads.pdf")}
        </Button>
        {artefacts?.png && (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={busyIcon("png", <DownloadRounded />)}
            onClick={() => download("png")}
            disabled={downloading !== null}
          >
            {t("detail.downloads.png")}
          </Button>
        )}
        {children}
      </Box>
      {(ext === "docx" || ext === "pptx") && (
      <Typography
        sx={{
          mt: 1,
          fontSize: 12,
          color: color.gray500,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <InfoOutlined sx={{ fontSize: 14 }} />
        {t("detail.downloads.fontsHint", { ext: ext.toUpperCase() })}
      </Typography>
      )}

      <PdfViewerDialog
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        url={documentCraftingFileUrls.revisionFile(documentId, revisionId, "pdf")}
        title={viewerTitle}
        onDownload={() => download("pdf")}
        downloading={downloading === "pdf"}
      />
    </Box>
  );
};

export default RevisionDownloads;
